import { ComponentStyleConfig } from '@chakra-ui/react';
import { getColorVar } from '@chakra-ui/theme-tools';
import { createVariables } from '../../../util';

const variables = createVariables<
  | 'bg'
  | 'glow'
  | 'height'
  | 'icon'
  | 'stroke'
  | 'thumbInset'
  | 'thumbSize'
  | 'thumbX'
  | 'width'
>();

export const SwitchStyles: ComponentStyleConfig = {
  parts: ['container', 'track', 'thumb'],
  baseStyle: (props) => ({
    container: {
      width: `var(${variables.width})`,
      height: `var(${variables.height})`,
      [variables.bg]: getColorVar(props.theme, 'button.gray'),
      [variables.glow]: 'transparent',
      [variables.icon]: getColorVar(props.theme, 'text.secondary'),
      [variables.stroke]: getColorVar(props.theme, 'button.gray.outline'),
      [variables.thumbInset]: `calc((var(${variables.height}) - var(${variables.thumbSize})) / 2)`,
      [variables.thumbX]: `var(${variables.thumbInset})`,
      _hover: {
        [variables.bg]: getColorVar(props.theme, 'button.gray.hover'),
      },
      _active: {
        [variables.bg]: getColorVar(props.theme, 'button.gray.active'),
      },
      _checked: {
        [variables.thumbX]: `calc(var(${variables.width}) - var(${variables.thumbSize}) - var(${variables.thumbInset}))`,
        [variables.icon]: getColorVar(props.theme, 'state.selected'),
        [variables.glow]: `var(${variables.icon})`,
      },
      _invalid: {
        [variables.icon]: getColorVar(props.theme, 'button.red'),
        [variables.stroke]: getColorVar(props.theme, 'button.red.outline'),
      },
      '&:has([data-readonly],[data-disabled])': {
        '&&&': {
          [variables.bg]: getColorVar(props.theme, 'button.gray'),
          [variables.stroke]: getColorVar(props.theme, 'button.gray.outline'),
        },
        [variables.glow]: 'transparent',
        [variables.icon]: getColorVar(props.theme, 'state.disabled'),
        cursor: 'not-allowed',
        opacity: 0.5,
        '& :not(#\\#):not(#\\#)': {
          pointerEvents: 'none',
        },
      },
    },
    track: {
      p: 0,
      w: 'full',
      h: 'full',
      boxSizing: 'border-box',
      alignItems: 'center',
      outline: '2px solid transparent',
      outlineOffset: '1px',
      boxShadow: `inset 0 0 0 1px var(${variables.stroke})`,
      background: `var(${variables.bg})`,
      backgroundImage: `radial-gradient(circle at center right, color-mix(in srgb, var(${variables.glow}) 40%, transparent), transparent 45%)`,

      _focusVisible: {
        outlineColor: 'state.selected',
      },

      _disabled: {
        opacity: 1,
      },
    },
    thumb: {
      w: `var(${variables.thumbSize})`,
      h: `var(${variables.thumbSize})`,
      bg: `var(${variables.icon})`,
      transitionProperty: 'background,transform',
      '&&': {
        transform: `translateX(var(${variables.thumbX}))`,
      },
    },
  }),
  variants: {
    box: (_props) => {
      return {
        track: {
          borderRadius: 5,
        },
        thumb: {
          height: '100%',
          width: '50%',
          borderRadius: 4,
          _checked: {
            transform: 'translateX(100%)',
          },
        },
      };
    },
  },
  sizes: {
    xs: {
      container: {
        [variables.width]: 'sizes.6',
        [variables.height]: 'sizes.3',
        [variables.thumbSize]: 'sizes.2',
      },
    },
    sm: {
      container: {
        [variables.width]: 'sizes.7',
        [variables.height]: 'sizes.4',
        [variables.thumbSize]: '10px',
      },
    },
    md: {
      container: {
        [variables.width]: 'sizes.9',
        [variables.height]: 'sizes.5',
        [variables.thumbSize]: 'sizes.3',
      },
    },
    lg: {
      container: {
        [variables.width]: 'sizes.12',
        [variables.height]: 'sizes.7',
        [variables.thumbSize]: 'sizes.4',
      },
    },
  },
};
